// Abgelaufene Anmeldung still erneuern (ST-UX-001, NFR-UX-005).
//
// Ein Zugriffstoken kann zwischen Abruf und Antwort ablaufen; der Server sagt
// dann 401. Statt "gerade nicht erreichbar" holt der Rahmen ein frisches Token
// und wiederholt die Anfrage genau einmal. Erst wenn das ohne Interaktion nicht
// geht, startet die Neuanmeldung; liefert auch die Wiederholung 401, kommt der
// Fehlersatz des Aufrufers -- keine Schleife.
//
// Nur fuer LESENDE Abrufe: ein Befehl wird nicht still wiederholt.
//
// Die Anmeldung meldet main.tsx einmal an. Eine Erneuerung gilt fuer alle
// gleichzeitigen 401 (Filter und Suche laden parallel): MSAL erlaubt nur eine
// Interaktion zur Zeit, und die Nutzerin soll nur einmal umgeleitet werden.

/** Wie `accessToken` in main.tsx; `erneuern` umgeht den Zwischenspeicher von MSAL. */
export type TokenQuelle = (optionen?: { readonly erneuern?: boolean }) => Promise<string | undefined>;

type Senden = (input: string, init?: RequestInit) => Promise<Response>;

interface Anmeldung {
  readonly accessToken: TokenQuelle;
  readonly onReauthenticate: () => void;
}

let anmeldung: Anmeldung | undefined;
let laufend: Promise<string | undefined> | undefined;

/** Setzt die Anmeldung des Rahmens; `undefined` beim Abmelden. */
export function setzeAnmeldung(neu: Anmeldung | undefined): void {
  anmeldung = neu;
  laufend = undefined;
}

function erneuern(aktuell: Anmeldung): Promise<string | undefined> {
  if (laufend !== undefined) return laufend;
  const diese: Promise<string | undefined> = (async () => {
    // Nur "ohne Interaktion geht es nicht" (undefined) fuehrt zur Neuanmeldung.
    // Ein Netz- oder Konfigurationsfehler geht an den Aufrufer, der seinen
    // behebbaren Fehlersatz zeigt, statt die Nutzerin ins Anmelden zu schicken.
    const token = await aktuell.accessToken({ erneuern: true });
    if (token === undefined) aktuell.onReauthenticate();
    return token;
  })().finally(() => {
    // Nach einem Kontowechsel laeuft womoeglich schon eine neue Erneuerung.
    if (laufend === diese) laufend = undefined;
  });
  laufend = diese;
  return diese;
}

/** Ein `send` fuer lesende Abrufe, das ein 401 mit frischem Token einmal wiederholt. */
export function mitAnmeldung(send: Senden = (input, init) => fetch(input, init)): Senden {
  return async (input, init) => {
    // Die Anmeldung, unter der die Anfrage losging, erneuert sie auch.
    const aktuell = anmeldung;
    const antwort = await send(input, init);
    if (antwort.status !== 401 || aktuell === undefined) return antwort;
    const token = await erneuern(aktuell);
    if (token === undefined) return antwort;
    init?.signal?.throwIfAborted();
    // Nur der Kopf wechselt; Signal, Methode und Rumpf bleiben die des Aufrufers.
    const headers = new Headers(init?.headers);
    headers.set('authorization', `Bearer ${token}`);
    return send(input, { ...init, headers });
  };
}
